import React, { useEffect } from 'react';

import { Colors, createClasses } from '@kp/react-ui';

import { useDashboardScope } from '@/hooks/useDashboardScope';

import { ChatHeader } from './ChatHeader';
import { ChatInput } from './ChatInput';
import { ChatMessages } from './ChatMessages';
import { CHAT_FAB_OFFSET, CHAT_Z_INDEX } from './constants';
import { useChatState } from './hooks/useChatState';
import { useResizable } from './hooks/useResizable';

const classes = createClasses({
  root: {
    position: 'fixed',
    bottom: `${CHAT_FAB_OFFSET}px`,
    right: `${CHAT_FAB_OFFSET}px`,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    backgroundColor: Colors.Neutral.backgroundMedium,
    boxShadow: `0 0 16px color-mix(in srgb, ${Colors.Neutral.borderHeavy} 12%, transparent)`,
    overflow: 'hidden',
    zIndex: CHAT_Z_INDEX,
    animationName: 'chatPanelIn',
    animationDuration: '0.2s',
    animationTimingFunction: 'ease-out',
    animationFillMode: 'forwards',
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '16px',
    height: '16px',
    cursor: 'nw-resize',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 'none',
    background: 'none',
    outline: 'none',
  },
  '@global': {
    '@keyframes chatPanelIn': {
      '0%': {
        opacity: 0,
        transform: 'translateY(16px) scale(0.9)',
      },
      '100%': {
        opacity: 1,
        transform: 'translateY(0) scale(1)',
      },
    },
  },
});

interface ChatPanelProps {
  onClose: () => void;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ onClose }) => {
  const { spaceId, spaceType, timeSpan } = useDashboardScope();
  const {
    messages,
    isLoading,
    loadingText,
    model,
    setModel,
    sendMessage,
    retry,
  } = useChatState({ spaceId, spaceType, timeSpan });
  const { width, height, startResize } = useResizable();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className={classes.root}
      style={{
        width: `${width}px`,
        height: `${height}px`,
      }}
      data-testid="chat-panel"
    >
      <button
        type="button"
        className={classes.resizeHandle}
        onMouseDown={startResize}
        data-testid="chat-resize-handle"
        aria-label="Resize chat panel"
      />
      <ChatHeader onClose={onClose} model={model} onModelChange={setModel} />
      <ChatMessages
        messages={messages}
        isLoading={isLoading}
        loadingText={loadingText}
        onSelectPrompt={sendMessage}
        onRetry={retry}
      />
      <ChatInput onSend={sendMessage} disabled={isLoading} />
    </div>
  );
};
